import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import GitConfig from '@/components/ui/GitConfig'
import IngressConfig from '@/components/ui/IngressConfig'
import ServiceConfig from '@/components/ui/ServiceConfig'
import PVCConfig from '@/components/ui/PVCConfig'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import { projectService } from '@/services/projectService'
import { defaultFlowConfig, defaultEnv, defaultIngress } from '@/services/default'
import { HttpError } from '@/lib/httpError'
import CreateFlowConfirmModal, { type CreateFlowConfirmModalRef } from '@/components/modals/CreateFlowConfirmModal'
import ProjectLayout from '@/components/ProjectLayout'

export default function ProjectCreateFlowPage() {
  const { t } = useTranslation(['pipeline-studio', 'common'])

  const STEPS = [
    { id: 1, name: t('createPage.steps.basicInfo.name'), description: t('createPage.steps.basicInfo.description') },
    { id: 2, name: t('createPage.steps.general.name'), description: t('createPage.steps.general.description') },
    { id: 3, name: t('createPage.steps.production.name'), description: t('createPage.steps.production.description') }
  ]
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()
  const confirmModalRef = useRef<CreateFlowConfirmModalRef>(null)

  const [project, setProject] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',

    // Step 2: General Configuration
    serviceConfig: {
      type: 'ClusterIP',
      port: 1880,
      targetPort: 1880,
      nodePort: undefined,
      loadBalancerIP: '',
      annotations: {}
    },
    pvcConfig: {
      enabled: false,
      pvcName: ''
    },

    // Step 3: Production Settings
    gitConfig: {
      enabled: false,
      repoURL: '',
      branch: 'main',
      skipSSLVerify: false,
      credentialsSecret: ''
    },
    ingressConfig: {
      enabled: false,
      host: '',
      tls: {
        enabled: false,
        secretName: defaultIngress.tlsSecretName
      },
      className: defaultIngress.className,
      annotations: {}
    }
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Check permissions
  // Creating a pipeline is reaching the editor, so it belongs to the project's
  // owner like opening one does (BAFTT-16). A project admin keeps the right to
  // ask for one, which is not access but a request the owner answers.
  //
  // The platform role is gone from all three. Every client administrator here is
  // created a platform owner, so reading the role would have let one client act
  // on another's project.
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'
  const canManageFlows = isProjectOwner || isProjectAdmin
  const canDirectCreate = isProjectOwner
  const canRequestCreate = isProjectOwner || isProjectAdmin

  // Fetch project info
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) return

      try {
        setLoading(true)
        const data = await projectService.getProjectById(projectId)
        setProject(data)
      } catch (err) {
        if (err instanceof HttpError && err.status === 401) {
          try {
            await refreshAuth()
            const data = await projectService.getProjectById(projectId)
            setProject(data)
          } catch (refreshError) {
            console.error('Failed to fetch project info:', refreshError)
            navigate(`/projects/${projectId}/products/pipeline-studio`)
          }
        } else {
          console.error('Failed to fetch project:', err)
          navigate(`/projects/${projectId}/products/pipeline-studio`)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchProjectInfo()
  }, [projectId, refreshAuth, navigate])

  // If user doesn't have permission, show access denied
  if (!loading && !canManageFlows) {
    return (
      <ProjectLayout>
        <div className="max-w-4xl mx-auto p-6 h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-7V8m0 0V6m0 2H9m3 0h3M5 12a7 7 0 1114 0A7 7 0 015 12z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('createPage.accessDenied.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('createPage.accessDenied.description')}
            </p>
            <button
              onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio`)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {t('createPage.accessDenied.backToList')}
            </button>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = t('createPage.errors.nameRequired')
      } else if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(formData.name.trim())) {
        newErrors.name = t('createPage.errors.nameInvalid')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length))
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const buildFlowManifest = () => {
    const spec: any = {
      replicas: defaultFlowConfig.replicas,
      image: defaultFlowConfig.image,
      imagePullPolicy: defaultFlowConfig.imagePullPolicy,
      env: defaultEnv,
    }
    spec.env.variables.AUTH_PROJECT_ID = projectId

    // Service configuration
    if (formData.serviceConfig) {
      spec.service = {
        type: formData.serviceConfig.type || 'ClusterIP',
        port: formData.serviceConfig.port,
        targetPort: formData.serviceConfig.targetPort,
      }

      if (formData.serviceConfig.nodePort !== undefined) {
        spec.service.nodePort = formData.serviceConfig.nodePort
      }
      if (formData.serviceConfig.loadBalancerIP) {
        spec.service.loadBalancerIP = formData.serviceConfig.loadBalancerIP
      }
      if (formData.serviceConfig.annotations && Object.keys(formData.serviceConfig.annotations).length > 0) {
        spec.service.annotations = formData.serviceConfig.annotations
      }
    }

    // Storage/PVC configuration
    if (formData.pvcConfig?.enabled) {
      spec.storage = {
        enabled: true,
        deletionPolicy: 'Retain' as const,
      }

      if (formData.pvcConfig.pvcName) {
        spec.storage.existingPVCName = formData.pvcConfig.pvcName
      } else {
        spec.storage.size = defaultFlowConfig.storage.size
      }
    }

    // Git configuration
    if (formData.gitConfig?.enabled) {
      spec.git = {
        enabled: true,
        repoURL: formData.gitConfig.repoURL,
        branch: formData.gitConfig.branch || 'main',
        skipSSLVerify: formData.gitConfig.skipSSLVerify || false,
      }

      if (formData.gitConfig.credentialsSecret) {
        spec.git.credentialsSecret = formData.gitConfig.credentialsSecret
      }
    }

    // Ingress configuration
    if (formData.ingressConfig?.enabled) {
      spec.ingress = {
        enabled: true,
        host: formData.ingressConfig.host,
        className: formData.ingressConfig.className || defaultIngress.className,
      }

      if (formData.ingressConfig.tls?.enabled) {
        spec.ingress.tls = {
          enabled: true,
        }
        if (formData.ingressConfig.tls.secretName) {
          spec.ingress.tls.secretName = formData.ingressConfig.tls.secretName
        }
      }

      if (formData.ingressConfig.annotations && Object.keys(formData.ingressConfig.annotations).length > 0) {
        spec.ingress.annotations = formData.ingressConfig.annotations
      }
    }

    const manifest = {
      apiVersion: 'synstream.rtsinv.com/v1',
      kind: 'Flow',
      metadata: {
        name: formData.name.trim(),
        namespace: project.name,
        annotations: {
          'synstream.console/author': user?.name || 'unknown'
        }
      },
      spec
    }

    if (formData.description.trim()) {
      (manifest.metadata.annotations as Record<string, string>)['synstream.console/description'] = formData.description.trim()
    }

    return manifest
  }

  const handleSubmit = () => {
    if (!validateStep(currentStep)) return

    const manifest = buildFlowManifest()

    confirmModalRef.current?.open({
      name: formData.name.trim(),
      namespace: project.name,
      manifest
    })
  }

  const handleSuccess = () => {
    navigate(`/projects/${projectId}/products/pipeline-studio`)
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleServiceConfigChange = (serviceConfig: any) => {
    setFormData(prev => ({ ...prev, serviceConfig }))
  }

  const handlePVCConfigChange = (pvcConfig: any) => {
    setFormData(prev => ({ ...prev, pvcConfig }))
  }

  const handleGitConfigChange = (gitConfig: any) => {
    setFormData(prev => ({ ...prev, gitConfig }))
  }

  const handleIngressConfigChange = (ingressConfig: any) => {
    setFormData(prev => ({ ...prev, ingressConfig }))
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('createPage.steps.basicInfo.title')}</h3>
              <p className="text-sm text-gray-600 mb-6">{t('createPage.steps.basicInfo.subtitle')}</p>
            </div>

            {/* Flow Name */}
            <div>
              <label htmlFor="flowName" className="block text-sm font-medium text-text-dark mb-2">
                {t('createPage.fields.flowName')} <span className="text-red-500">*</span>
              </label>
              <input
                id="flowName"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary transition-all duration-200 ${
                  errors.name ? 'border-red-300 focus:ring-red-200 focus:border-red-500' : 'border-secondary-300 hover:border-primary-400'
                }`}
                placeholder={t('createPage.fields.flowNamePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="flowDescription" className="block text-sm font-medium text-text-dark mb-2">
                {t('createPage.fields.description')}
              </label>
              <textarea
                id="flowDescription"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2.5 border rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary transition-all duration-200 resize-none border-secondary-300 hover:border-primary-400"
                placeholder={t('createPage.fields.descriptionPlaceholder')}
              />
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('createPage.steps.general.title')}</h3>
              <p className="text-sm text-gray-600 mb-6">{t('createPage.steps.general.subtitle')}</p>
            </div>

            {/* Service Configuration */}
            <ServiceConfig
              value={formData.serviceConfig}
              onChange={handleServiceConfigChange}
              disabled={false}
            />

            {/* PVC Configuration */}
            <PVCConfig
              value={formData.pvcConfig}
              onChange={handlePVCConfigChange}
              disabled={false}
              namespace={project?.name}
            />
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">{t('createPage.steps.production.subtitle')}</h4>
                  <p className="text-sm text-blue-700">
                    {t('createPage.steps.production.notice')}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('createPage.steps.production.title')}</h3>
            </div>

            {/* Ingress Configuration */}
            <div>
              <IngressConfig
                value={formData.ingressConfig}
                onChange={handleIngressConfigChange}
                disabled={false}
                namespace={project?.name}
              />
            </div>

            {/* Git Configuration */}
            <div className="mt-6">
              <GitConfig
                value={formData.gitConfig}
                onChange={handleGitConfigChange}
                disabled={false}
                namespace={project?.name}
              />
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (loading) {
    return (
      <ProjectLayout>
        <LoadingOverlay message={t('createPage.loadingProject')} fullScreen />
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout>
      <div className="max-w-5xl mx-auto p-6 pb-20">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio`)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('createPage.title')}</h1>
              <p className="text-sm text-gray-600">{t('createPage.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center flex-shrink-0">
                  <div
                    className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                      currentStep === step.id
                        ? 'border-primary bg-primary text-white'
                        : currentStep > step.id
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-sm font-semibold">{step.id}</span>
                    )}
                  </div>
                  <div className="ml-3 hidden md:block">
                    <div className={`text-sm font-semibold whitespace-nowrap ${currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'}`}>
                      {step.name}
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">{step.description}</div>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 mx-4 bg-gray-200">
                    <div
                      className={`h-full transition-all duration-300 ${
                        currentStep > step.id ? 'bg-primary' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('createPage.buttons.previous')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              {t('createPage.buttons.cancel')}
            </button>

            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {t('createPage.buttons.next')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-700 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('createPage.buttons.createFlow')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Create Flow Confirm Modal */}
        <CreateFlowConfirmModal
          ref={confirmModalRef}
          canDirectCreate={canDirectCreate}
          canRequestCreate={canRequestCreate}
          onSuccess={handleSuccess}
        />
      </div>
    </ProjectLayout>
  )
}
